import { Carousel } from '@mantine/carousel'
import { Skeleton, Box } from '@mantine/core'
import PropTypes from 'prop-types'
import { getText } from '../utils/constants'
import TitleDivider from './divider'
import NextSmallSVG from './buttons/next_carousel_button_small'
import PreviousSmallSVG from './buttons/previous_carousel_button_small'
import CharacterCard from './character_card'
import Link from 'next/link'

const CharacterCarousel = ({ cast, isLoading, title }) => {
  const t = (key) => getText('CharacterCarousel', key)

  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }} p="md">
        {[...Array(5)].map((_, index) => (
          <Skeleton key={index} height={200} radius="md" animate={true} />
        ))}
      </Box>
    )
  }

  if (!cast || cast.length === 0) {
    return null
  }

  const mergedCast = Object.values(
    cast.reduce((acc, actor) => {
      if (!acc[actor.name]) {
        acc[actor.name] = { ...actor, roles: [actor.role] }
      } else {
        if (!acc[actor.name].roles.includes(actor.role)) {
          acc[actor.name].roles.push(actor.role)
        }
      }
      return acc
    }, {})
  ).map((actor) => ({
    ...actor,
    role: actor.roles.join(', ')
  }))

  if (mergedCast.length === 0) {
    return null
  }

  return (
    <Box mt={24}>
      <TitleDivider titleText={`CAST OF ${title.toUpperCase()}`} epgText="" />

      <Carousel
        height={300}
        w="100%"
        mt={30}
        slideGap="sm"
        slideSize="10%"
        loop={false}
        align="start"
        slidesToScroll={5}
        nextControlIcon={<NextSmallSVG top={-140} width="100" height="280" />}
        previousControlIcon={
          <PreviousSmallSVG top={-140} width="100" height="280" />
        }
        styles={{
          control: {
            opacity: 1,
            cursor: 'default',
            backgroundColor: 'transparent',
            border: 'transparent',
            transform: 'none',
            transition: 'none',

            '&[data-inactive]': {
              opacity: 0,
              cursor: 'default',
              visibility: 'hidden',
              pointerEvents: 'none',
              width: 0,
              margin: 0
            },
            '@media (max-width: 768px)': {
              display: 'none'
            }
          }
        }}
      >
        {mergedCast.map((actor, index) => {
          const uniqueKey = `${actor.number_id}-${index}`
          return (
            <Carousel.Slide key={uniqueKey}>
              <Link href={`/search?q=${actor.name}`} prefetch={false} passHref>
                <CharacterCard
                  name={
                    actor.name.length > 15
                      ? actor.name.slice(0, 15) + '...'
                      : actor.name
                  }
                  src={actor.profile_path}
                  character={actor.character || t('unknownCharacter')}
                  role={actor.role}
                  episodes={actor.episodes || 0}
                />
              </Link>
            </Carousel.Slide>
          )
        })}
      </Carousel>
    </Box>
  )
}

CharacterCarousel.propTypes = {
  cast: PropTypes.arrayOf(
    PropTypes.shape({
      number_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      character: PropTypes.string,
      profile_path: PropTypes.string,
      role: PropTypes.string,
      episodes: PropTypes.number
    })
  ).isRequired,
  isLoading: PropTypes.bool
}

export default CharacterCarousel
